import { useState, useCallback } from 'react';
import { Geolocation } from '@capacitor/geolocation';

import { isNativePlatform } from 'src/utils/capacitor';

type Coordinates = {
  lat: number;
  lng: number;
};

type AddressFields = {
  address: string;
  area: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
};

type LocationResult = {
  coordinates: Coordinates;
} & AddressFields;

// Cache key: coordinates rounded to 4 decimal places
function getCacheKey(lat: number, lng: number): string {
  return `geocode_${lat.toFixed(4)}_${lng.toFixed(4)}`;
}

function getCachedGeocode(lat: number, lng: number): AddressFields | null {
  if (typeof window === 'undefined') return null;
  const key = getCacheKey(lat, lng);
  const cached = localStorage.getItem(key);
  if (!cached) return null;
  try {
    return JSON.parse(cached);
  } catch {
    return null;
  }
}

function setCachedGeocode(lat: number, lng: number, fields: AddressFields): void {
  if (typeof window === 'undefined') return;
  const key = getCacheKey(lat, lng);
  localStorage.setItem(key, JSON.stringify(fields));
}

/**
 * Reverse geocode coordinates using Nominatim (free, no API key)
 */
async function reverseGeocode(lat: number, lng: number): Promise<AddressFields> {
  // Check cache first
  const cached = getCachedGeocode(lat, lng);
  if (cached) return cached;

  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'ArthamFintech/1.0',
        },
      }
    );

    if (!response.ok) {
      throw new Error('Geocoding request failed');
    }

    const data = await response.json();
    const addr = data.address || {};

    const fields: AddressFields = {
      address:
        [addr.road, addr.house_number].filter(Boolean).join(', ') || addr.neighbourhood || '',
      area: addr.suburb || addr.neighbourhood || addr.village || '',
      city: addr.city || addr.town || addr.county || addr.state_district || '',
      state: addr.state || '',
      country: addr.country || 'India',
      pincode: addr.postcode || '',
    };

    // Cache the result
    setCachedGeocode(lat, lng, fields);

    return fields;
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    return {
      address: '',
      area: '',
      city: '',
      state: '',
      country: 'India',
      pincode: '',
    };
  }
}

/**
 * Coordinates only, no reverse geocoding. Split out of the hook so callers that
 * just need a fix - a DMT transfer, where Eko fraud-checks `latlong` and our own
 * geofence gate needs it - do not pay for a geocode round-trip on the send path.
 */
export async function getCurrentCoordinates(): Promise<Coordinates> {
  if (isNativePlatform()) {
    // Android does not prompt from getCurrentPosition - an ungranted permission
    // just throws. Ask first; already-granted returns immediately.
    const status = await Geolocation.checkPermissions();
    if (status.location !== 'granted') {
      const asked = await Geolocation.requestPermissions();
      if (asked.location !== 'granted' && asked.coarseLocation !== 'granted') {
        throw new Error('Location permission denied');
      }
    }
    const position = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: 10000,
    });
    return { lat: position.coords.latitude, lng: position.coords.longitude };
  }
  return new Promise<Coordinates>((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({ lat: position.coords.latitude, lng: position.coords.longitude }),
      (geoError) => reject(geoError),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });
}

export function useCurrentLocation() {
  const [coordinates, setCoordinates] = useState<Coordinates | null>(null);
  const [addressFields, setAddressFields] = useState<AddressFields | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getCurrentLocation = useCallback(async (): Promise<LocationResult | null> => {
    setLoading(true);
    setError(null);

    try {
      const coords = await getCurrentCoordinates();

      setCoordinates(coords);

      // Reverse geocode
      const fields = await reverseGeocode(coords.lat, coords.lng);
      setAddressFields(fields);

      return { coordinates: coords, ...fields };
    } catch (err: any) {
      const message = err?.message || 'Unable to get your location';
      setError(message);
      console.error('Error getting current location:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    coordinates,
    addressFields,
    loading,
    error,
    getCurrentLocation,
    reverseGeocode,
  };
}
